import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';

import { LISTING_STATE_DRAFT, DELIVERY, PICKUP } from '../../util/types';
import { ensureListing } from '../../util/data';
import { EditListingFeaturesForm } from '../../forms';
import { ListingLink } from '../../components';

import { types as sdkTypes } from '../../util/sdkLoader';

import css from './EditListingFeaturesPanel.css';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

const { Money } = sdkTypes;

const EditListingFeaturesPanel = memo(
  props => {
    const {
      rootClassName,
      className,
      listing,
      disabled,
      ready,
      onSubmit,
      onBackWithoutSubmit,
      onChange,
      submitButtonText,
      panelUpdated,
      updateInProgress,
      errors,
      intl,
    } = props;

    const classes = classNames(rootClassName || css.root, className);
    const currentListing = ensureListing(listing);
    const { publicData } = currentListing.attributes;

    const isPublished =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
    const panelTitle = isPublished ? (
      <FormattedMessage
        id="EditListingFeaturesPanel.title"
        values={{ listingTitle: <ListingLink listing={listing} /> }}
      />
    ) : (
      <FormattedMessage id="EditListingFeaturesPanel.createListingTitle" />
    );

    const { deliveryType = null, deliveryFee, deliveryLocations } = publicData || {};
    const locationsVal = (deliveryLocations || [])
      .map(locationVal => {
        const { address, origin, suburb } = locationVal;
        const locationFieldsPresent = address && origin;
        return locationFieldsPresent
          ? {
            search: address,
            selectedPlace: { address, origin },
            suburb,
          }
          : null;
      })
      .filter(locationVal => !!locationVal);

    const initialValues = {
      deliveryType: deliveryType || [PICKUP],
      deliveryFee:
        deliveryFee && deliveryFee.amount !== undefined && deliveryFee.currency
          ? new Money(deliveryFee.amount, deliveryFee.currency)
          : null,
      locations: locationsVal && locationsVal.length > 0 ? locationsVal : [null],
    };

    return (
      <div className={classes}>
        <h1 className={css.title}>{panelTitle}</h1>
        <EditListingFeaturesForm
          className={css.form}
          initialValues={initialValues}
          onSubmit={values => {
            const { deliveryType, deliveryFee, locations, typeSubmit } = values;

            const deliveryLocations = (locations || [{}]).map(location => {
              const { selectedPlace, suburb } = location || {};
              const { address, origin } = selectedPlace || {};
              return { address, origin, suburb };
            });

            const updatedValues = {
              publicData: {
                deliveryType,
                deliveryFee:
                  deliveryType &&
                    deliveryType.includes(DELIVERY) &&
                    deliveryFee &&
                    deliveryFee.amount !== undefined &&
                    deliveryFee.currency
                    ? { amount: deliveryFee.amount, currency: deliveryFee.currency }
                    : null,
                deliveryLocations:
                  deliveryType &&
                    deliveryType.includes(DELIVERY) &&
                    deliveryLocations &&
                    deliveryLocations[0] &&
                    deliveryLocations[0].address
                    ? deliveryLocations
                    : null,
              },
              typeSubmit,
            };
            onSubmit(updatedValues);
          }}
          onBackWithoutSubmit={onBackWithoutSubmit}
          onChange={onChange}
          saveActionMsg={submitButtonText}
          disabled={disabled}
          ready={ready}
          updated={panelUpdated}
          updateInProgress={updateInProgress}
          fetchErrors={errors}
          intl={intl}
        />
      </div>
    );
  },
  (prevProps, nextProps) =>
    isEqual(
      pick(prevProps, [
        'listing',
        'disabled',
        'ready',
        'panelUpdated',
        'updateInProgress',
        'errors',
      ]),
      pick(nextProps, [
        'listing',
        'disabled',
        'ready',
        'panelUpdated',
        'updateInProgress',
        'errors',
      ])
    )
);

EditListingFeaturesPanel.defaultProps = {
  rootClassName: null,
  className: null,
  listing: null,
};

const { bool, func, object, string } = PropTypes;

EditListingFeaturesPanel.propTypes = {
  rootClassName: string,
  className: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingFeaturesPanel;
